import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Radio, Save, CalendarClock, Users, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { AdminNav, useAdminAuth } from "../components/AdminNav";
import { API } from "../lib/scroll";
import { errMsg } from "../context/FanContext";
import { fmtEur } from "../config/site";
import { fmtDateTime, STATUS_META } from "../lib/pay";
import { fmtLiveDate } from "../components/LiveSection";

const F = "h-11 rounded-xl border-[rgba(242,215,208,0.16)] bg-[#111114] text-[#f5f2ee] placeholder:text-[#6f6a64]";
const L = ({ children }) => <div className="text-[10px] uppercase tracking-[0.18em] text-[#b9b2aa]">{children}</div>;
const PHASE = { none: ["Nessuna diretta in programma", "text-[#8a847d]"], scheduled: ["Programmata", "text-[color:var(--gold-light)]"], live: ["In corso adesso", "text-[#ff8aa0]"], ended: ["Terminata: imposta la prossima", "text-[#ffcc66]"] };

// datetime-local lavora in ora locale del browser: convertiamo da/verso ISO UTC
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export default function AdminLive() {
  const { token, headers, handle, nav } = useAdminAuth();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ starts_at: "", duration_min: 60, seats_total: 10, hold_minutes: 120, new_session: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) { nav("/admin"); return; }
    try {
      const { data } = await axios.get(`${API}/admin/live`, { headers });
      setS(data);
      setForm({ starts_at: toLocalInput(data.starts_at), duration_min: data.duration_min, seats_total: data.seats_total, hold_minutes: data.hold_minutes, new_session: !data.starts_at || data.phase === "ended" });
    } catch (e) { if (!handle(e)) toast.error(errMsg(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async (clear = false) => {
    setBusy(true);
    try {
      const body = { starts_at: clear ? null : fromLocalInput(form.starts_at), duration_min: Number(form.duration_min) || 60, seats_total: Number(form.seats_total) || 10, hold_minutes: Number(form.hold_minutes) || 120, new_session: !!form.new_session };
      if (!clear && !body.starts_at) { toast.error("Imposta data e ora"); setBusy(false); return; }
      const { data } = await axios.put(`${API}/admin/live`, body, { headers });
      setS(data);
      setForm((f) => ({ ...f, starts_at: toLocalInput(data.starts_at), new_session: false }));
      toast.success(clear ? "Diretta rimossa dalla home" : "Diretta salvata");
    } catch (e) { if (!handle(e)) toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!s) return <div className="min-h-screen bg-[#0b0b0d]"><AdminNav /><div className="flex justify-center pt-32"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--gold)]" /></div></div>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const [phaseLabel, phaseCls] = PHASE[s.phase] || PHASE.none;
  const pct = s.seats_total ? Math.round((s.seats_taken / s.seats_total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-[#f5f2ee]" data-testid="admin-live-page">
      <AdminNav onRefresh={load} />
      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8">
        <h1 className="flex items-center gap-3 font-display text-4xl italic"><Radio className="h-7 w-7 text-[#ff4d6d]" /> Diretta</h1>
        <p className="mt-1 text-sm text-[#b9b2aa]">Imposti data, ora e posti. In home compaiono countdown e posti liberi veri: ogni ordine prende un posto, gli ordini non pagati lo liberano dopo il tempo di blocco.</p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="admin-live-status">
          <div className="lux-card p-4"><L>Stato</L><div className={`mt-1 font-display text-xl italic ${phaseCls}`} data-testid="admin-live-phase">{phaseLabel}</div>{s.starts_at && <div className="mt-1 text-xs text-[#b9b2aa]">{fmtLiveDate(s.starts_at)}</div>}</div>
          <div className="lux-card p-4"><L>Posti</L><div className="mt-1 font-display text-xl italic" data-testid="admin-live-seats">{s.seats_taken} prenotati · {s.seats_left} liberi</div><Progress value={pct} className="mt-2 h-1.5 bg-[rgba(255,77,109,0.15)] [&>div]:bg-[#ff4d6d]" /></div>
          <div className="lux-card p-4"><L>Prezzo accesso</L><div className="mt-1 font-display text-xl italic">{fmtEur(s.price_eur || 0)}</div><div className="mt-1 text-xs text-[#8a847d]">Si cambia in Testi & prezzi</div></div>
        </div>

        <section className="lux-card mt-6 space-y-4 p-6" data-testid="admin-live-form">
          <h2 className="flex items-center gap-2 font-display text-2xl italic"><CalendarClock className="h-5 w-5 text-[color:var(--gold)]" /> Prossima diretta</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2"><L>Data e ora (ora italiana)</L><Input data-testid="live-starts-at" type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} className={F} /></div>
            <div className="space-y-2"><L>Posti totali</L><Input data-testid="live-seats-total" inputMode="numeric" value={form.seats_total} onChange={(e) => set("seats_total", e.target.value)} className={F} /></div>
            <div className="space-y-2"><L>Durata (minuti)</L><Input data-testid="live-duration" inputMode="numeric" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className={F} /></div>
            <div className="space-y-2"><L>Blocco posto se non pagato (minuti)</L><Input data-testid="live-hold" inputMode="numeric" value={form.hold_minutes} onChange={(e) => set("hold_minutes", e.target.value)} className={F} /></div>
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[rgba(0,175,240,0.14)] bg-[#111114] p-3">
            <Checkbox data-testid="live-new-session" checked={form.new_session} onCheckedChange={(v) => set("new_session", !!v)} className="mt-0.5 border-[color:var(--gold)] data-[state=checked]:bg-[color:var(--gold)] data-[state=checked]:text-[#0b0b0d]" />
            <span className="text-sm text-[#e9e4de]">Nuova diretta: azzera i posti prenotati (gli ordini gia' pagati restano validi come acquisti). <span className="text-xs text-[#8a847d]">Lascia spento se stai solo spostando orario o posti della stessa diretta.</span></span>
          </label>
          <div className="flex flex-wrap gap-2">
            <button data-testid="live-save" onClick={() => save(false)} disabled={busy} className="btn-gold flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold uppercase tracking-[0.14em] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva diretta</button>
            <button data-testid="live-clear" onClick={() => save(true)} disabled={busy || !s.starts_at} className="btn-ghost-lux flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-5 text-xs uppercase tracking-[0.14em] disabled:opacity-40"><Trash2 className="h-4 w-4" /> Nessuna diretta</button>
          </div>
        </section>

        <section className="lux-card mt-6 p-6" data-testid="admin-live-booked">
          <h2 className="flex items-center gap-2 font-display text-2xl italic"><Users className="h-5 w-5 text-[color:var(--gold)]" /> Prenotazioni ({s.booked?.length || 0})</h2>
          {!s.booked?.length && <p className="mt-3 text-sm text-[#8a847d]">Nessun posto prenotato per questa diretta.</p>}
          <ul className="mt-3 divide-y divide-[rgba(242,215,208,0.08)]">
            {s.booked?.map((b) => (
              <li key={b.code} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`live-booking-${b.code}`}>
                <span className="font-mono text-[color:var(--gold-light)]">{b.code}</span>
                <span className="text-[#f5f2ee]">@{b.fan_username}</span>
                <span className="text-xs text-[#b9b2aa]">{fmtDateTime(b.created_at)}</span>
                <span className="text-xs uppercase tracking-[0.14em] text-[#b9b2aa]">{STATUS_META[b.status]?.short || b.status}</span>
                <span className="tabular-nums">{fmtEur(b.total_eur)}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}
